import { useInfiniteQuery, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { getPublishHistory } from 'src/api/getPublishHistory';
import type { IGetPublishHistoryParams, IGetPublishHistoryResponse } from 'src/api/getPublishHistory';
import { ScheduleStatus } from 'src/api/getSchedules/types';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';

const PENDING_POLL_INTERVAL_MS = 5000;

export const usePublishHistory = (
  params: Omit<IGetPublishHistoryParams, 'afterId'> = {},
  options?: UseInfiniteQueryOptions<IGetPublishHistoryResponse>,
) => {
  const { showSnackbar } = useSnackbar();

  return useInfiniteQuery<IGetPublishHistoryResponse>({
    queryKey: [getBrandId(), QueryKeys.publishHistory, params],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      getPublishHistory({
        ...params,
        afterId: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.paging.next ?? undefined,
    refetchOnWindowFocus: false,
    refetchInterval: (data: InfiniteData<IGetPublishHistoryResponse> | undefined) => {
      const isAnyPending = data?.pages.flatMap((p) => p.data).some((item) => item.status === ScheduleStatus.PENDING);
      return isAnyPending ? PENDING_POLL_INTERVAL_MS : false;
    },
    onError: (error: Error) => showSnackbar({ status: SnackbarStatus.ERROR, message: error.message }),
    ...options,
  });
};
